import { MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { useTestSlack, useUpdateSlack } from '@/hooks/use-settings'
import type { SlackSettings } from '@/lib/types'

interface SlackIntegrationCardProps {
  settings: SlackSettings
}

export function SlackIntegrationCard({ settings }: SlackIntegrationCardProps): React.JSX.Element {
  const updateSlack = useUpdateSlack()
  const testSlack = useTestSlack()

  const [webhookUrl, setWebhookUrl] = useState(settings.webhook_url)
  const [channel, setChannel] = useState(settings.channel)
  const [enabled, setEnabled] = useState(settings.enabled)

  const hasChanges =
    webhookUrl !== settings.webhook_url ||
    channel !== settings.channel ||
    enabled !== settings.enabled

  function handleSave(): void {
    updateSlack.mutate({ webhook_url: webhookUrl, channel, enabled })
  }

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="mb-4 flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-admin-500" />
        <h2 className="text-sm font-medium text-text-primary">Slack Integration</h2>
      </div>
      <div className="space-y-3">
        <div>
          <label
            htmlFor="slack-webhook-url"
            className="block text-xs font-medium text-text-secondary"
          >
            Webhook URL
          </label>
          <input
            id="slack-webhook-url"
            type="url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://hooks.slack.com/services/..."
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary"
          />
        </div>
        <div>
          <label htmlFor="slack-channel" className="block text-xs font-medium text-text-secondary">
            Channel
          </label>
          <input
            id="slack-channel"
            type="text"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            placeholder="#alerts"
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-text-primary">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="rounded"
          />
          Enable Slack notifications
        </label>
        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateSlack.isPending || !hasChanges}
            className="rounded-lg bg-admin-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-admin-600 disabled:opacity-50"
          >
            {updateSlack.isPending ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => testSlack.mutate()}
            disabled={testSlack.isPending || !webhookUrl}
            className="rounded-lg border border-surface-3 px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-surface-2 disabled:opacity-50"
          >
            {testSlack.isPending ? 'Sending...' : 'Test Slack'}
          </button>
          {testSlack.isSuccess && <span className="text-xs text-green-600">Test message sent</span>}
        </div>
      </div>
    </div>
  )
}
